import { useParams, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import API_URL from "@/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import { Label } from "@/components/ui/label";

const resetSchema = z
    .object({
        password: z.string()
            .min(6, "Password must be at least 6 characters")
            .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
            .regex(/[a-z]/, "Password must contain at least one lowercase letter")
            .regex(/[0-9]/, "Password must contain at least one number")
            .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character")
        ,
        confirmPassword: z.string().min(6, "Confirm Password must be at least 6 characters"),
    })
    .refine((data) => data.password === data.confirmPassword, {
        path: ["confirmPassword"],
        message: "Passwords do not match",
    });

type ResetForm = z.infer<typeof resetSchema>;

export default function ResetPassword() {
    const { token } = useParams<{ token: string }>();
    const { toast } = useToast();
    const [_, navigate] = useLocation();

    const form = useForm<ResetForm>({
        resolver: zodResolver(resetSchema),
    });

    const onSubmit = async (data: ResetForm) => {
        try {
            const res = await axios.post(`${API_URL}/api/auth/reset-password/${token}`, {
                password: data.password,
            });

            if (res.data.success) {
                toast({ variant: "success", title: "Password reset successfully!" });
                navigate("/login");
            }
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error",
                description: error?.response?.data?.message || "Failed to reset password",
            });
        }
    };

    return (
        <div>
            <Header />
            <div className="flex items-center justify-center" style={{ minHeight: "80vh" }}>
                <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="bg-white p-6 rounded-xl shadow-md w-96"
                >
                    <h2 className="text-2xl font-bold mb-4">Set New Password</h2>

                    {/* Password field */}
                    <div className="space-y-3">
                        <div>
                            <Label htmlFor="password">New Password</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="New Password"
                                {...form.register("password")}
                                className="mb-1"
                            />
                            {form.formState.errors.password && (
                                <p className="text-sm text-red-600">
                                    {form.formState.errors.password.message}
                                </p>
                            )}
                        </div>

                        {/* Confirm Password field */}
                        <div>
                            <Label htmlFor="confirm-password">Confirm Password</Label>
                            <Input
                                id="confirm-password"
                                type="password"
                                placeholder="Confirm New Password"
                                {...form.register("confirmPassword")}
                                className="mb-1"
                            />
                            {form.formState.errors.confirmPassword && (
                                <p className="text-sm text-red-600">
                                    {form.formState.errors.confirmPassword.message}
                                </p>
                            )}
                        </div>
                    </div>

                    <Button type="submit" className="w-full mt-4">
                        Reset Password
                    </Button>
                </form>
            </div>
        </div>
    );
}
